import React, { useEffect } from "react";
import { Modal, Form, Select, InputNumber, DatePicker, Row, Col, Switch } from "antd";
import { useCreate, useUpdate } from "../../../../hooks";
import { masterDataService } from "../../../../api/enhancedApi";
import dayjs from "dayjs";
import { toFormDate, toUtcExclusiveEndIso, toUtcStartOfDayIso } from "../lib/exchangeRatePeriod";

const { Option } = Select;

const ExchangeRateForm = ({
    visible,
    onCancel,
    onSuccess,
    editingRecord = null,
    currencies = [],
}) => {
    const [form] = Form.useForm();
    const isEditing = !!editingRecord;

    // 使用自定义Hook处理创建和更新
    const createMutation = useCreate("汇率", masterDataService.exchangeRate.createExchangeRate, {
        onSuccess: () => {
            onSuccess();
            form.resetFields();
        },
    });

    const updateMutation = useUpdate("汇率", masterDataService.exchangeRate.updateExchangeRate, {
        onSuccess: () => {
            onSuccess();
            form.resetFields();
        },
    });

    // 初始化表单数据
    useEffect(() => {
        if (visible) {
            if (isEditing && editingRecord) {
                form.setFieldsValue({
                    from_currency: editingRecord.from_currency,
                    to_currency: editingRecord.to_currency,
                    rate: editingRecord.rate,
                    effective_from: toFormDate(editingRecord.effective_from),
                    effective_to: toFormDate(editingRecord.effective_to, { end: true }),
                    source: editingRecord.source,
                    status: editingRecord.record_status === "ACTIVE",
                });
            } else {
                // 新建模式：重置表单
                form.resetFields();
            }
        }
    }, [visible, isEditing, editingRecord, form]);

    // 提交表单
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const submitData = {
                ...values,
                rate: typeof values.rate === "string" ? parseFloat(values.rate) : values.rate,
                effective_from: toUtcStartOfDayIso(values.effective_from),
                effective_to: values.effective_to ? toUtcExclusiveEndIso(values.effective_to) : null,
                status: values.status ? "ACTIVE" : "INACTIVE",
            };

            if (isEditing) {
                updateMutation.mutate({
                    id: editingRecord.id,
                    data: submitData,
                });
            } else {
                createMutation.mutate(submitData);
            }
        } catch (error) {
            console.error("表单验证失败:", error);
            // 输出详细的验证错误信息
            if (error.errorFields && error.errorFields.length > 0) {
                error.errorFields.forEach(field => {
                    console.error(`字段 ${field.name.join('.')} 验证失败:`, field.errors);
                });
            }
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 汇率来源选项
    const sourceOptions = [
        { value: "CENTRAL_BANK", label: "央行汇率" },
        { value: "COMMERCIAL_BANK", label: "商业银行" },
        { value: "API", label: "API接口" },
        { value: "MANUAL", label: "手动录入" },
        { value: "MIGRATED", label: "数据迁移" },
    ];

    return (
        <Modal
            title={isEditing ? "编辑汇率" : "新增汇率"}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={createMutation.isLoading || updateMutation.isLoading}
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    source: "MANUAL", // 默认为手动录入
                    status: true, // 默认启用
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="from_currency"
                            label="源货币"
                            rules={[
                                { required: true, message: "请选择源货币" },
                            ]}
                        >
                            <Select
                                placeholder="请选择源货币"
                                showSearch
                                optionFilterProp="label"
                                disabled={isEditing}
                            >
                                {currencies.map((currency) => (
                                    <Option key={currency.code} value={currency.code} label={`${currency.code} - ${currency.name}`}>
                                        {currency.code} - {currency.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="to_currency"
                            label="目标货币"
                            rules={[
                                { required: true, message: "请选择目标货币" },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('from_currency') !== value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('源货币和目标货币不能相同'));
                                    },
                                }),
                            ]}
                        >
                            <Select
                                placeholder="请选择目标货币"
                                showSearch
                                optionFilterProp="label"
                                disabled={isEditing}
                            >
                                {currencies.map((currency) => (
                                    <Option key={currency.code} value={currency.code} label={`${currency.code} - ${currency.name}`}>
                                        {currency.code} - {currency.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="rate"
                            label="汇率"
                            rules={[
                                { required: true, message: "请输入汇率" },
                                {
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.resolve(); // required规则会处理空值
                                        }
                                        
                                        // 将字符串转换为数字进行验证
                                        const numValue = typeof value === 'string' ? parseFloat(value) : value;
                                        
                                        if (isNaN(numValue)) {
                                            return Promise.reject(new Error('请输入有效的数字'));
                                        }
                                        
                                        if (numValue < 0.000001 || numValue > 999999.999999) {
                                            return Promise.reject(new Error('汇率必须在 0.000001 - 999999.999999 之间'));
                                        }
                                        
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <InputNumber
                                placeholder="请输入汇率"
                                style={{ width: "100%" }}
                                precision={6}
                                step={0.000001}
                                stringMode // 使用字符串模式避免精度问题
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="source"
                            label="汇率来源"
                            rules={[
                                { required: true, message: "请选择汇率来源" },
                            ]}
                        >
                            <Select placeholder="请选择汇率来源">
                                {sourceOptions.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="status"
                            label="状态"
                            valuePropName="checked"
                        >
                            <Switch 
                                checkedChildren="启用" 
                                unCheckedChildren="禁用"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="effective_from"
                            label="生效开始日期"
                            rules={[
                                { required: true, message: "请选择生效开始日期" },
                            ]}
                        >
                            <DatePicker
                                format="YYYY-MM-DD"
                                placeholder="请选择生效开始日期"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="effective_to"
                            label="生效结束日期"
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || !getFieldValue("effective_from")) {
                                            return Promise.resolve();
                                        }
                                        const fromDay = dayjs(getFieldValue("effective_from")).format("YYYY-MM-DD");
                                        const toDay = dayjs(value).format("YYYY-MM-DD");
                                        if (toDay >= fromDay) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error("结束日期不能早于开始日期"));
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                format="YYYY-MM-DD"
                                placeholder="请选择生效结束日期（可选）"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="说明"
                    style={{ marginBottom: 0 }}
                >
                    <div style={{ color: "#666", fontSize: "12px" }}>
                        • 结算按日历日取汇率，开始、结束都只选日期<br />
                        • 结束日期当天仍有效；空着表示一直有效到被新汇率替换<br />
                        • 同一货币对的有效期间不能重叠<br />
                        • 汇率支持最多6位小数
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ExchangeRateForm;
